import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MobileStepper,
  useTheme,
  alpha,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  AddCircle as AddIcon,
  Menu as MenuIcon,
  School as CoursesIcon,
  Payments as PaymentsIcon,
  Person as ProfileIcon,
  Home as HomeIcon,
  Close as CloseIcon,
  ArrowForward,
  ArrowBack,
} from "@mui/icons-material";
import { useAuth } from "../../pages/authentication/AuthContext";

const GUIDE_DISABLED_KEY = "moxy_onboarding_disabled_v1";

const getSteps = (role) => [
  {
    icon: <HomeIcon sx={{ fontSize: 56 }} />,
    title: "Welcome to Moxy Driving School!",
    description:
      "This quick guide will show you how to navigate the app. You can dismiss it anytime or choose not to see it again.",
    color: "#1976d2",
  },
  {
    icon: <AddIcon sx={{ fontSize: 56 }} />,
    title: "The + Button",
    description:
      "On mobile, tap the + button (centre of the bottom bar) to open the sidebar. The sidebar gives you access to all sections of the app.",
    color: "#2e7d32",
  },
  {
    icon: <MenuIcon sx={{ fontSize: 56 }} />,
    title: "Sidebar Navigation",
    description:
      "The sidebar lists every page: Dashboard, My Courses, Payments, Support, and more. On desktop the sidebar is always visible on the left.",
    color: "#e65100",
  },
  {
    icon: <CoursesIcon sx={{ fontSize: 56 }} />,
    title: role === "student" ? "My Enrollments" : "My Courses",
    description:
      role === "student"
        ? 'Find all the courses you are enrolled in under "My Enrollments" in the bottom bar or sidebar. Tap a course to view schedules and session details.'
        : 'Find all your courses under "My Courses" in the sidebar. Tap a course to view schedules and session details.',
    color: "#6a1b9a",
  },
  {
    icon: <PaymentsIcon sx={{ fontSize: 56 }} />,
    title: "Payments & Fees",
    description:
      'Your fee balance and payment history are under "Payments". You can make online payments securely via M-Pesa or card.',
    color: "#c62828",
  },
  {
    icon: <ProfileIcon sx={{ fontSize: 56 }} />,
    title: "Profile & Support",
    description:
      "Update your profile anytime from the Profile page. If you need help, visit Support to send a request directly to your instructor or the management team.",
    color: "#00695c",
  },
];

const OnboardingGuide = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `${GUIDE_DISABLED_KEY}_${user.uid || user.id}`;
    if (!localStorage.getItem(key)) {
      setOpen(true);
    }
  }, [user]);

  const dismiss = () => {
    if (dontShowAgain) {
      const key = `${GUIDE_DISABLED_KEY}_${user?.uid || user?.id}`;
      localStorage.setItem(key, "1");
    }
    setOpen(false);
    setActiveStep(0);
    setDontShowAgain(false);
  };

  const steps = getSteps(user?.role);
  const step = steps[activeStep];
  const isLast = activeStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Coloured header strip */}
      <Box
        sx={{
          bgcolor: step.color,
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "white",
          position: "relative",
        }}
      >
        <IconButton
          onClick={dismiss}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8, color: "white", opacity: 0.7 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ color: "white", mb: 1 }}>{step.icon}</Box>
        <Typography
          variant="caption"
          sx={{ color: alpha("#fff", 0.7), letterSpacing: 1, textTransform: "uppercase" }}
        >
          Step {activeStep + 1} of {steps.length}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 1, textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700} mb={1.5}>
          {step.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
          {step.description}
        </Typography>
      </DialogContent>

      {/* Dot stepper */}
      <MobileStepper
        variant="dots"
        steps={steps.length}
        position="static"
        activeStep={activeStep}
        sx={{
          justifyContent: "center",
          bgcolor: "transparent",
          py: 1,
          "& .MuiMobileStepper-dot": { width: 8, height: 8, mx: 0.5 },
          "& .MuiMobileStepper-dotActive": { bgcolor: step.color },
        }}
        backButton={null}
        nextButton={null}
      />

      {/* Don't show again */}
      <Box sx={{ px: 3, pb: 0.5, display: "flex", justifyContent: "center" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              sx={{ color: step.color, "&.Mui-checked": { color: step.color } }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Don't show this again
            </Typography>
          }
        />
      </Box>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: "space-between" }}>
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep((s) => s - 1)}
          size="small"
          startIcon={<ArrowBack />}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>

        {isLast ? (
          <Button
            variant="contained"
            onClick={dismiss}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              bgcolor: step.color,
              "&:hover": { bgcolor: step.color, filter: "brightness(0.9)" },
            }}
          >
            Get Started
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={() => setActiveStep((s) => s + 1)}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              bgcolor: step.color,
              "&:hover": { bgcolor: step.color, filter: "brightness(0.9)" },
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingGuide;
